import React from 'react';
import { LuArrowUpDown, LuChevronDown, LuChevronUp } from 'react-icons/lu';

import { SortDirection } from '@/hooks/useSimpleTableSort';

import { Button } from './ui/button';
import { TableHead } from './ui/table';

/**
 * Simple sortable table header component
 *
 * Use this with useSimpleTableSort for lightweight table sorting.
 * Renders a TableHead with a clickable button that shows sort state icons.
 *
 * For complex tables, use ClientDataTable instead.
 *
 * @example
 * <SimpleSortableHeader
 *   column="name"
 *   label="Name"
 *   sortColumn={sortColumn}
 *   sortDirection={sortDirection}
 *   onSort={handleSort}
 *   className="min-w-[150px]"
 * />
 */

interface SimpleSortableHeaderProps<T extends string> {
    column: T;
    label: string;
    sortColumn: T | null;
    sortDirection: SortDirection;
    onSort: (column: T) => void;
    className?: string;
}

export function SimpleSortableHeader<T extends string>({
    column,
    label,
    sortColumn,
    sortDirection,
    onSort,
    className,
}: SimpleSortableHeaderProps<T>) {
    const getSortIcon = () => {
        if (sortColumn !== column) {
            return <LuArrowUpDown className="ml-1 h-3 w-3" />;
        }
        return sortDirection === 'asc' ? (
            <LuChevronUp className="ml-1 h-3 w-3" />
        ) : (
            <LuChevronDown className="ml-1 h-3 w-3" />
        );
    };

    return (
        <TableHead className={className}>
            <Button
                variant="ghost"
                size="sm"
                className="hover:text-cocoa-1000 h-auto p-0 font-medium text-gray-700 hover:bg-transparent"
                onClick={() => onSort(column)}
            >
                {label}
                {getSortIcon()}
            </Button>
        </TableHead>
    );
}
